import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Eye, FileSpreadsheet, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SurveiIsiVersi from "@/components/SurveiIsiVersi";
import SurveiEditorVersi from "@/components/SurveiEditorVersi";
import SurveiPratinjauFormulir from "@/components/SurveiPratinjauFormulir";
import { api, downloadBlobResponse, formatApiErrorDetail } from "@/lib/api";
import { LABEL_STATUS_VERSI, STATUS_DRAF, teksDipakai } from "@/lib/surveiVersi";
import { butirPublikDariEditor, drafKeEditor } from "@/lib/surveiEditor";

// Tab "Butir" (Super Admin): daftar versi + aksi (Lihat, Salin ke draf baru, Edit/Hapus khusus draf, Unduh Excel) &
// templat Excel; "Lihat" = isi baca-saja, "Edit" = SurveiEditorVersi. `versi` = GET /survei/versi-butir (dimuat
// SurveiAdmin, dipakai juga form periode); `onBerubah` memuat ulang daftar itu.
// `bacaSaja` (Admin UFP): hanya Lihat + Pratinjau formulir; salin/edit/hapus/templat/ekspor tidak dirender (server menolak 403).
export default function SurveiButirVersi({ versi, onBerubah = () => {}, bacaSaja = false }) {
    const [pratinjau, setPratinjau] = useState(false);
    const [tampil, setTampil] = useState({ mode: "daftar" }); // {mode: "daftar"|"lihat"|"edit", id}
    const [isi, setIsi] = useState(null);
    const [hapus, setHapus] = useState(null);
    useEffect(() => {
        if (tampil.mode !== "lihat") return undefined;
        let batal = false;
        setIsi(null);
        api.get(`/survei/versi-butir/${tampil.id}`)
            .then((r) => { if (!batal) setIsi(r.data); })
            .catch((e) => { if (!batal) toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
        return () => { batal = true; };
    }, [tampil]);

    const unduh = async (url, cadangan) => {
        try {
            downloadBlobResponse(await api.get(url, { responseType: "blob" }), cadangan);
        } catch { toast.error("Gagal menyiapkan berkas. Coba lagi beberapa saat."); }
    };
    const salin = async (v) => {
        try {
            const res = await api.post(`/survei/versi-butir/${v.id}/salin`);
            toast.success(`Draf ${res.data.label} dibuat dari ${v.label}`);
            onBerubah();
            setTampil({ mode: "edit", id: res.data.id });
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const jalankanHapus = async () => {
        try {
            await api.delete(`/survei/versi-butir/${hapus.id}`);
            toast.success(`Draf ${hapus.label} dihapus`);
            onBerubah();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setHapus(null);
    };
    const keDaftar = () => setTampil({ mode: "daftar" });

    if (tampil.mode === "edit") return <SurveiEditorVersi versiId={tampil.id} onKembali={keDaftar} onBerubah={onBerubah} />;
    if (tampil.mode === "lihat") {
        return (
            <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={keDaftar} data-testid="survei-lihat-kembali">← Daftar versi</Button>
                    {bacaSaja && isi && (
                        <Button variant="outline" size="sm" onClick={() => setPratinjau(true)} data-testid="survei-lihat-pratinjau">
                            <Eye className="mr-1 h-4 w-4" /> Pratinjau formulir
                        </Button>
                    )}
                </div>
                <Card className="p-4">{isi ? <SurveiIsiVersi versi={isi} /> : <p className="text-sm text-slate-500">Memuat…</p>}</Card>
                {pratinjau && isi && <SurveiPratinjauFormulir butir={butirPublikDariEditor(drafKeEditor(isi), isi)} onTutup={() => setPratinjau(false)} />}
            </div>
        );
    }
    return (
        <div className="space-y-4" data-testid="survei-butir">
            <Card className="overflow-hidden">
                {!bacaSaja && <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
                    <p className="text-xs text-slate-500">
                        Versi terkunci atau yang sudah dipakai tidak bisa diubah; revisi dibuat lewat "Salin ke draf baru".
                    </p>
                    <Button size="sm" variant="outline" onClick={() => unduh("/survei/versi-butir/templat.xlsx", "survei-templat-butir.xlsx")} data-testid="survei-unduh-templat">
                        <FileSpreadsheet className="mr-1 h-4 w-4" /> Unduh templat Excel
                    </Button>
                </div>}
                <div className="overflow-x-auto">
                    <table className="compact w-full" data-testid="survei-tabel-versi">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                                <th>Versi</th><th>Label</th><th>Status</th><th className="text-right">Butir</th><th className="text-right">Dipakai</th><th className="pr-4 text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {versi.map((v) => {
                                const draf = v.status === STATUS_DRAF;
                                return (
                                    <tr key={v.id} className="border-t border-slate-200" data-testid={`survei-versi-${v.nomor_versi}`}>
                                        <td className="font-semibold text-slate-800">{v.nama}</td>
                                        <td className="text-xs">{v.label}</td>
                                        <td><Badge variant="outline">{LABEL_STATUS_VERSI[v.status] || v.status}</Badge></td>
                                        <td className="text-right tabular-nums">
                                            {v.jumlah_butir}
                                            <div className="text-[11px] text-slate-500" data-testid={`survei-versi-tb-${v.nomor_versi}`}>
                                                {v.opsi_tb?.aktif ? `${v.opsi_tb.label}: ${v.jumlah_boleh_tb}` : "TB nonaktif"}
                                            </div>
                                        </td>
                                        <td className="text-right text-xs">{teksDipakai(v.dipakai_periode)}</td>
                                        <td className="whitespace-nowrap pr-2 text-right">
                                            <Button size="sm" variant="ghost" aria-label={`Lihat ${v.label}`} onClick={() => setTampil({ mode: "lihat", id: v.id })} data-testid={`survei-lihat-${v.nomor_versi}`}><Eye className="h-4 w-4" /></Button>
                                            {!bacaSaja && <Button size="sm" variant="ghost" aria-label={`Salin ${v.label} ke draf baru`} onClick={() => salin(v)} data-testid={`survei-salin-${v.nomor_versi}`}><Copy className="h-4 w-4" /></Button>}
                                            {!bacaSaja && draf && <Button size="sm" variant="ghost" aria-label={`Edit ${v.label}`} onClick={() => setTampil({ mode: "edit", id: v.id })} data-testid={`survei-edit-${v.nomor_versi}`}><Pencil className="h-4 w-4" /></Button>}
                                            {!bacaSaja && draf && !v.dipakai_periode && (
                                                <Button size="sm" variant="ghost" className="text-rose-600" aria-label={`Hapus ${v.label}`} onClick={() => setHapus(v)} data-testid={`survei-hapus-${v.nomor_versi}`}><Trash2 className="h-4 w-4" /></Button>
                                            )}
                                            {!bacaSaja && <Button size="sm" variant="ghost" aria-label={`Unduh Excel ${v.label}`} onClick={() => unduh(`/survei/versi-butir/${v.id}/ekspor.xlsx`, "survei-butir.xlsx")} data-testid={`survei-unduh-versi-${v.nomor_versi}`}><Download className="h-4 w-4" /></Button>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>
            <AlertDialog open={!!hapus} onOpenChange={(o) => !o && setHapus(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus draf {hapus?.label}?</AlertDialogTitle>
                        <AlertDialogDescription>Draf belum pernah dipakai dan akan dihapus permanen. Riwayat dan Audit Log tetap tersimpan.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={jalankanHapus} data-testid="survei-hapus-ya">Ya, hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
